import {endOfToday, isAfter} from 'date-fns';
import {ReviewStage, ReviewStatusOneviewEnum} from '../../schema-types/globalTypes';
import {ReviewDetailsFragment} from '../../schema-types/ReviewDetailsFragment';
import {getReviewSubmissionRedirect} from '../../utils/getReviewSubmissionURL';
import {handoverStepRoutes} from './HandoverStepTypes';

export const reviewMustStopAtPrecursor = (reviewDetails: ReviewDetailsFragment) => {
  if (reviewDetails.precursorNotFinished) {
    return true;
  }
  if (reviewDetails.stage === ReviewStage.NEED_BOOKING) {
    return true;
  }
  if (reviewDetails.stage === ReviewStage.BOOKED) {
    const dateBooked = new Date(reviewDetails.dateBooked);
    const endToday = endOfToday();
    if (isAfter(dateBooked, endToday)) {
      return true;
    }
  }
  return false;
};

/**
 * Pure resolver for which review-journey tubestop a review should land on.
 * Shared by the index redirect page and the precursor submission flow so the
 * decision is computed identically from whatever review state is passed in.
 */
export const getHandoverTubestopRoute = (
  reviewDetails: ReviewDetailsFragment,
  canUseReportPack: boolean
): handoverStepRoutes => {
  const {name, stage, statusOneview, postcursorNotFinished} = reviewDetails;

  const isSPP = name?.includes('SPP Review');

  const isCompletedStage =
    stage === ReviewStage.AWAITING_CLIENT ||
    stage === ReviewStage.LIVE_CASES ||
    statusOneview === ReviewStatusOneviewEnum.CLOSED ||
    statusOneview === ReviewStatusOneviewEnum.COMPLETED;

  if (isSPP) {
    return handoverStepRoutes.requirements;
  }
  if (isCompletedStage) {
    return canUseReportPack ? handoverStepRoutes.report : handoverStepRoutes.summary;
  }
  if (reviewMustStopAtPrecursor(reviewDetails)) {
    return handoverStepRoutes.meeting;
  }
  if (postcursorNotFinished) {
    return handoverStepRoutes.resilience;
  }
  return handoverStepRoutes.meeting;
};

/**
 * Where to navigate after the precursor is submitted.
 *
 * The next tubestop is computed from the freshly-mutated review passed in here
 * (which carries the just-persisted `precursorNotFinished`), rather than
 * bouncing through HandoverIndexRedirectPage — that page re-derives the
 * destination from the ReviewContext cache, which can still hold the
 * pre-submit `precursorNotFinished: true` and circle the adviser back to the
 * start (ON-9166).
 */
export const getPrecursorSubmissionRedirect = ({
  reviewDetails,
  canUseReportPack,
}: {
  reviewDetails: ReviewDetailsFragment;
  canUseReportPack: boolean;
}): string => {
  const submissionPath = getReviewSubmissionRedirect({reviewDetails, canUseReportPack});
  const indexPath = `/account/${reviewDetails.account?.id}/review-journey/${reviewDetails.id}`;

  // Declined / no-response / no-advice exit the journey (reviews list or report
  // pack) — getReviewSubmissionRedirect already returns that dedicated path.
  if (submissionPath !== indexPath) {
    return submissionPath;
  }

  // Normal case: resolve the concrete tubestop from the fresh review state.
  return `${indexPath}/${getHandoverTubestopRoute(reviewDetails, canUseReportPack)}`;
};
